import {
  Calendar,
  LayoutDashboard,
  MessageSquarePlus,
  Palette,
} from "lucide-react";
import { Constants } from "src/constant";
import type { Announcement } from "src/lib/announcements";
import { canManageService, ServiceDomain } from "src/lib/roles";
import {
  formatNewThemeNames,
  getCurrentlyNewThemes,
  THEME_ANNOUNCEMENT_ID,
} from "src/lib/themeOptions";

// snooze for 1 day
const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Ordered registry of code-defined announcements. The first eligible entry is
 * shown after login (see {@link AnnouncementCenter}).
 *
 * To announce something new, add an entry with a fresh `id`. To re-show an
 * edited announcement, bump its `id`. Entries are eligible until dismissed;
 * add an `isActive` predicate to auto-expire (as the theme entry does).
 *
 * Options per entry:
 *  - `snoozeMs` — adds a "Not now" button that hides it for that long.
 *  - `repeat` + `frequencyDays` — recurring reminder instead of one-time,
 *    e.g. `repeat: true, frequencyDays: 7` returns weekly after each dismiss.
 *  - `primary.to` / `primary.href` / `primary.onClick` — where the button
 *    goes: an internal route, an external link, or a custom handler.
 *
 * Built lazily so time-dependent copy (e.g. which themes are "new") is fresh
 * each time it's evaluated.
 */
export function getAnnouncements(): Announcement[] {
  return [
    {
      id: "2026-08-happening-now-composer",
      icon: LayoutDashboard,
      badge: "New",
      title: "A fresh Happening Now composer",
      body: "Drop a batch of images, tune each card in a carousel, and publish faster with the new composer.",
      primary: {
        label: "Open the composer",
        to: Constants.HAPPENING_NOW_COMPOSE_ROUTE,
      },
      snoozeMs: DAY_MS,
      // The composer is manage-only (its route's RoleGuard). Only nudge people
      // who can actually open it — never advertise a dead link.
      isActive: ({ role }) =>
        canManageService(role, ServiceDomain.HappeningNow),
    },
    {
      id: "2026-08-happening-now-schedule",
      icon: Calendar,
      badge: "New",
      title: "Schedule Happening Now cards",
      body: "Set a start time so a card only goes live when you want it to.",
      primary: {
        label: "Start scheduling",
        to: Constants.HAPPENING_NOW_COMPOSE_ROUTE,
      },
      snoozeMs: DAY_MS,
      isActive: ({ role }) =>
        canManageService(role, ServiceDomain.HappeningNow),
    },
    {
      id: "2026-08-feedback-launch",
      icon: MessageSquarePlus,
      badge: "New",
      title: "Tell us what you think",
      body: "Found a bug or have an idea? Send it straight to the team.",
      primary: {
        label: "Give feedback",
        to: "/settings?section=feedback",
      },
      snoozeMs: DAY_MS,
    },
    {
      id: THEME_ANNOUNCEMENT_ID,
      icon: Palette,
      badge: "New",
      title: "Fresh themes are here",
      body: `Try ${formatNewThemeNames(
        getCurrentlyNewThemes(),
      )} in Settings → Appearance and make the dashboard yours.`,
      primary: { label: "View themes", to: "/settings" },
      snoozeMs: DAY_MS,
      isActive: ({ nowMs }) => getCurrentlyNewThemes(nowMs).length > 0,
    },
  ];
}
